import { FC, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

import { appRoutes } from "@/app/router";
import { MdsPanelContainer } from "@/design-system/components/panel";
import { useAppStore } from "@/store";
import { AudioRecordingCoordinator } from "@/store/audio-capture/coordinator";
import type { AudioRecordingMetadata } from "@/domains/dexie-db/models/audio-recording";
import { MDSButton, MDSText, MDSTextVariant } from "@/mds";

interface RecordingListItem {
  recordingId: string;
  capturedAudioId?: string;
  noteId?: string;
  createdAt: number;
  status: string;
}

const buildListItem = (run: AudioRecordingMetadata): RecordingListItem => ({
  recordingId: run.recordingId,
  capturedAudioId: run.capturedAudioId,
  noteId: run.noteId,
  createdAt: run.createdAt,
  status: run.status,
});

export const AudioRecordingsPage: FC = observer(() => {
  const { store } = useAppStore();
  const coordinator = useMemo(
    () => new AudioRecordingCoordinator(store.memDb.audioRecordings),
    [store.memDb.audioRecordings]
  );
  const [recordings, setRecordings] = useState<RecordingListItem[]>([]);

  useEffect(() => {
    let cancelled = false;

    coordinator
      .listRuns({})
      .then(runs => {
        if (cancelled) return;
        const sorted = [...runs].sort((a, b) => b.createdAt - a.createdAt).map(buildListItem);
        setRecordings(sorted);
      })
      .catch(() => {
        if (!cancelled) setRecordings([]);
      });

    return () => {
      cancelled = true;
    };
  }, [coordinator]);

  return (
    <>
      <MdsPanelContainer>
        <header style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <h1 style={{ margin: 0 }}>Audio Recordings</h1>
          <MDSButton onClick={() => store.navigation.goToAudioRecordingCreate()}>
            New Recording
          </MDSButton>
        </header>
        {recordings.length === 0 ? (
          <MDSText variant={MDSTextVariant.TextSm}>No audio recordings found.</MDSText>
        ) : (
          <ul>
            {recordings.map(recording => (
              <li key={recording.recordingId}>
                <Link
                  to={
                    appRoutes.audioRecording({ params: { recordingId: recording.recordingId } })
                      .path
                  }
                >
                  {recording.capturedAudioId ?? recording.recordingId}
                </Link>
                {recording.noteId && <span> · Note: {recording.noteId}</span>}
                <span> · {new Date(recording.createdAt).toLocaleString()}</span>
                <span> · {recording.status}</span>
              </li>
            ))}
          </ul>
        )}
      </MdsPanelContainer>
    </>
  );
});

export default AudioRecordingsPage;
